import React, { useEffect, useState } from 'react';
import { Box, Fab, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import { GameAudioControls } from './GameAudioControls';
import { getAudioSettings, subscribeAudioSettings } from '../audio/gameAudio';

const FAB_SIZE = 48;
const EDGE = 'max(12px, env(safe-area-inset-right))';
const BOTTOM = 'max(12px, env(safe-area-inset-bottom))';

/** Floating action button that expands into the sound and voice controls, so nothing is reserved at the screen bottom. */
export function AudioFab({ children }) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(getAudioSettings);
  useEffect(() => subscribeAudioSettings(setSettings), []);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsidePress = (event) => {
      if (!event.target.closest?.('[data-audio-fab]')) setOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsidePress);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePress);
  }, [open]);

  const silent = !settings.music && !settings.soundEffects;

  return (
    <Box
      data-audio-fab
      component="nav"
      aria-label="Voice and game audio controls"
      sx={{
        position: 'fixed', right: EDGE, bottom: BOTTOM, zIndex: 10001,
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1,
        pointerEvents: 'none',
      }}
    >
      {/* Kept mounted while closed so voice chat stays connected and audio unlock listeners keep running. */}
      <Paper
        elevation={8}
        sx={{
          pointerEvents: open ? 'auto' : 'none',
          visibility: open ? 'visible' : 'hidden',
          opacity: open ? 1 : 0,
          transition: 'opacity 0.15s ease, visibility 0.15s',
          display: 'flex', alignItems: 'center', gap: 0.35, p: 0.5, borderRadius: 3,
          bgcolor: 'rgba(8,12,18,0.98)', border: '1px solid var(--app-border-emphasis)',
        }}
      >
        <GameAudioControls docked expanded={open} />
        {children}
      </Paper>
      <Fab
        size="medium"
        color="primary"
        aria-label={open ? 'Close sound controls' : 'Open sound controls'}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        sx={{
          pointerEvents: 'auto', width: FAB_SIZE, height: FAB_SIZE, minHeight: FAB_SIZE,
          opacity: open || !silent ? 1 : 0.75,
        }}
      >
        {open ? <CloseIcon /> : <GraphicEqIcon />}
      </Fab>
    </Box>
  );
}
